/*
 * Copyright (c) 2020. American Express Company
 */

import * as _ from 'lodash';
import { ChronoUnit, LocalDate } from '@js-joda/core';

import { BalanceHistory } from 'src/types/account.model';
import { CardModel } from 'src/types/card.model';
import { FullUserModel } from 'src/types/user.model';
import BWDataService from './bw-data.service';
import StorageService from './storage.service';
import MixpanelService from './mixpanel.service';
import { BW_PHANT_URL } from '../config';

export default class CardService extends BWDataService {
    private static readonly savingsGoals = [
        'save emergency fund',
        'save for baby',
        'save for business',
        'save for college',
        'save for education costs',
        'save for home',
        'save for large purchase',
        'save for retirement',
        'save for vacation',
        'save for wedding'
    ];

    private static readonly goalTypes: { [key: string]: string } = {
        '2008 crash': 'Moment',
        '2010 boom': 'Moment',
        'buy new home': 'Moment',
        'child starts college': 'Scenario',
        'collect social security': 'Moment',
        'donate a portion of your income': 'Scenario',
        'end of life': 'Moment',
        'estimate life insurance': 'Scenario',
        'get promoted': 'Moment',
        'have a baby': 'Scenario',
        'lose job': 'Moment',
        'new job': 'Moment',
        'oil shock': 'Moment',
        'pay down debt': 'Scenario',
        'quit job': 'Moment',
        'receive inheritance': 'Moment',
        'budget expenses': 'Scenario',
        'adjust expenses': 'Scenario',
        retire: 'Moment',
        'save for retirement': 'Savings',
        'save emergency fund': 'Savings',
        'save for baby': 'Savings',
        'save for business': 'Savings',
        'save for college': 'Savings',
        'save for home': 'Savings',
        'save for vacation': 'Savings',
        'save for wedding': 'Savings',
        'start business': 'Savings',
        'take a vacation': 'Moment',
        wedding: 'Moment',
        windfall: 'Moment',
        'care for a family member': 'Senario'
    };

    public static async deleteCard(card: CardModel): Promise<void> {
        try {
            await this.deleteRemoteCard(card.id);

            if (!card.description) {
                // when you drag you don't have the real card
                const cardObject = StorageService.getCard(card.id);
                if (cardObject) {
                    card = cardObject.card;
                }
            }
            const goalType: string = this.goalTypes[card.description];
            MixpanelService.track('Delete Goal', {
                'Goal Name': card.description,
                'Goal Type': goalType
            });

            StorageService.deleteCard(card.id);
        } catch (e) {
            console.error('failed in delete card', e);
        }
    }

    public static async createCard(
        cardToBeCreated: CardModel,
        user: FullUserModel,
        manual: boolean,
        mentorAdded: boolean
    ): Promise<CardModel> {
        // snap to start of month
        cardToBeCreated.startDate = cardToBeCreated.startDate.withDayOfMonth(1);
        cardToBeCreated.endDate = cardToBeCreated.endDate?.withDayOfMonth(1);

        const createdCard = await this.postCard(cardToBeCreated);
        StorageService.addCard(createdCard);

        const isRecommended: boolean = _.map(user.recommendedCards, (name) => {
            return name.toLowerCase();
        }).includes(createdCard.description);
        const goalType: string = this.goalTypes[cardToBeCreated.description];
        MixpanelService.track('Create Goal', {
            'Goal Name': createdCard.description,
            'Goal Type': goalType,
            'Recommended Goal': isRecommended,
            'Mentor Added': mentorAdded
        });
        return createdCard;
    }

    public static async updateCard(card: CardModel, user: FullUserModel = null): Promise<CardModel> {
        if (!user) {
            user = StorageService.getReduxUserObject();
        }

        let updatedCard: CardModel;
        let oldCard: CardModel = null;

        if (user) {
            oldCard = _.find(user.cards, { id: card.id });
        }

        // snap to start of month
        card.startDate = card.startDate.withDayOfMonth(1);
        card.endDate = card.endDate?.withDayOfMonth(1);

        if (oldCard) {
            if (!oldCard.accountId && card.accountId) {
                const goalType: string = this.goalTypes[card.description];
                MixpanelService.track('Link Account to Goal', {
                    'Goal Name': card.description,
                    'Goal Type': goalType
                });
            }
            if (oldCard.accountId && !card.accountId) {
                const goalType: string = this.goalTypes[card.description];
                MixpanelService.track('Unlink Account to Goal', {
                    'Goal Name': card.description,
                    'Goal Type': goalType
                });
            }

            oldCard.startDate = card.startDate;
            oldCard.endDate = card.endDate;
            oldCard.attributes = card.attributes;
            oldCard.accountId = card.accountId;
            oldCard.nickname = card.nickname || null;
            oldCard.startIncomes = card.startIncomes;
            oldCard.modifyIncomes = card.modifyIncomes;
            oldCard.investmentIds = card.investmentIds;
            oldCard.yPosition = card.yPosition;

            updatedCard = await this.putCard(oldCard);
        } else {
            updatedCard = await this.putCard(card);
        }

        StorageService.updateCard(updatedCard, user);
        return updatedCard;
    }

    public static getCard(cardId: number): CardModel {
        const user: FullUserModel = StorageService.getReduxUserObject();
        const cardData: { card: CardModel; cardIndex: number } = StorageService.getCard(cardId || 0, user);

        let card: CardModel = null as CardModel;
        try {
            card = user.cards[cardData.cardIndex];
        } catch (e) {
            console.log(e);
        }

        return card;
    }

    /** Get the edit/update drawer payload data for the card from the server. This contains additional information,
     * specific to each card and sometimes derived from cashflow or other tables, that is not in the smaller User.cards
     * data
     */
    public static async getCardPayload(card: CardModel): Promise<CardModel> {
        try {
            const cardPayload: CardModel = await this.get(`/cards/${card.id}`);
            if (cardPayload) {
                return CardModel.deserializeDates(cardPayload);
            }
        } catch (e) {
            console.log(e);
        }
        // fall back to just returning the card, without the extra payload data
        return card;
    }

    /** Create a linked savings card for the given Card. */
    public static async createLinkedSavingsCard(card: CardModel): Promise<CardModel> {
        try {
            const totalAmount = _.get(card, 'attributes.totalAmount');
            const response = await this.post(`/cards/savings/${card.id}`, {});
            let savingsCard: CardModel = CardModel.dateify(response);

            if (savingsCard) {
                savingsCard.attributes.targetBalance = _.isNumber(totalAmount) ? Math.abs(totalAmount) : 0;
                savingsCard = await CardService.updateCard(savingsCard);

                const deserialized = CardModel.deserializeDates(savingsCard);
                StorageService.addCard(deserialized);
                return deserialized;
            }
        } catch (err) {
            console.error('Creating savings card:', err);
            throw err;
        }
    }

    /** Delete a linked savings card for the given card */
    public static async deleteLinkedSavingsCard(card: CardModel): Promise<void> {
        // const savingsCard = this.getSavingsGoal(card);
        try {
            await this.delete(`/cards/savings/${card.id}`);
        } catch (err) {
            console.error('Deleting savings card:', err);
            throw err;
        }
    }

    public static async moveCard(
        card: CardModel,
        startDate: LocalDate,
        yPosition: number = card.yPosition || 0
    ): Promise<CardModel[]> {
        const offset = card.startDate.until(startDate, ChronoUnit.DAYS);

        card.startDate = startDate;
        card.endDate = card.endDate.plusDays(offset);
        card.yPosition = yPosition;

        const payload = { date: startDate, yPosition };
        try {
            const cards: CardModel[] = await this.put(`/cards/move/${card.id}`, payload);
            return _.map(cards, (newCard) => CardModel.deserializeDates(newCard));
        } catch (err) {
            console.error('Moving card:', err);
            throw err;
        }
    }

    /** update local storage and backend services with the movement of a card */
    public static async updateMovedCard(card: CardModel): Promise<CardModel> {
        return await this.putCard(card);
    }

    /** move card so that it is just before the stationaryCard */
    public static async moveCardBeforeAnother(stationaryCard: CardModel, card: CardModel): Promise<CardModel> {
        const duration = card.startDate.until(card.endDate, ChronoUnit.DAYS);
        card.endDate = stationaryCard.startDate;
        card.startDate = card.endDate.minusDays(duration);
        return await this.updateMovedCard(card);
    }

    /** move card so that it is just after the stationaryCard */
    public static async moveCardAfterAnother(stationaryCard: CardModel, card: CardModel): Promise<CardModel> {
        const duration = card.startDate.until(card.endDate, ChronoUnit.DAYS);
        card.startDate = stationaryCard.endDate;
        card.endDate = card.startDate.plusDays(duration);
        return await this.updateMovedCard(card);
    }

    private static async deleteRemoteCard(cardId: number): Promise<any> {
        await this.delete(`/cards/${cardId}`);
    }

    public static async putCard(card: CardModel): Promise<CardModel> {
        const payload: Partial<CardModel> = {
            description: card.description,
            startDate: card.startDate,
            endDate: card.endDate,
            attributes: { ...card.attributes },
            accountId: card.accountId,
            nickname: card.nickname || null,
            startIncomes: card.startIncomes,
            modifyIncomes: card.modifyIncomes,
            investmentIds: card.investmentIds,
            yPosition: card.yPosition,
            createdBy: card.createdBy
        };
        try {
            const updatedCard = await this.put(`/cards/${card.id}`, payload);
            return CardModel.deserializeDates(updatedCard);
        } catch (err) {
            console.error('Putting card:', err);
            throw err;
        }
    }

    private static async postCard(card: CardModel): Promise<CardModel> {
        const payload: Partial<CardModel> = {
            attributes: { ...card.attributes },
            description: card.description,
            nickname: card.nickname,
            startDate: card.startDate,
            endDate: card.endDate,
            startIncomes: [...card.startIncomes],
            modifyIncomes: [...card.modifyIncomes],
            yPosition: card.yPosition,
            createdBy: card.createdBy
        };
        try {
            const newCard = await this.post('/cards', payload);
            return CardModel.deserializeDates(newCard);
        } catch (e) {
            console.error('Failed to add card:', e);
            throw e;
        }
    }

    public static isSavingsGoal(card: CardModel): boolean {
        return card ? CardService.savingsGoals.includes(card.description) : false;
    }

    /**
     * Returns card associated with the given card, ie: 'save for home' and 'buy new home'
     * @remarks
     * updatedCard must not have it's changes saved at the time this function is called, otherwise it may no
     * longer be linked, because part of the link is that they share start/end dates
     * @param sourceCardId card.id of card to look for association
     * @returns associated card or undefined if no associated card
     */
    public static async getLinkedCardById(sourceCardId: number): Promise<CardModel> {
        const sourceCard: CardModel = StorageService.getCardModel(sourceCardId);
        return await CardService.getLinkedCard(sourceCard);
    }

    public static async getLinkedCard(sourceCard: CardModel): Promise<CardModel> {
        if (sourceCard?.attributes?.linked_card_id) {
            return CardService.getCard(sourceCard.attributes.linked_card_id);
        } else {
            return null;
        }
    }

    public static getLinkedCardFromUser(user: FullUserModel, sourceCard: CardModel): CardModel {
        if (sourceCard?.attributes?.linked_card_id) {
            return _.find(user.cards, { id: sourceCard.attributes.linked_card_id });
        } else {
            return null;
        }
    }

    public static getPeriod(card: CardModel): string {
        const years: number = card.startDate.until(card.endDate, ChronoUnit.YEARS);
        const newMarker: LocalDate = card.startDate.plusYears(years);
        const months: number = newMarker.until(card.endDate, ChronoUnit.MONTHS);

        switch (true) {
            case years === 0 && months === 0:
                return 'less than a month';
            case years > 0 && months === 0:
                return `${years} years`;
            case years > 0 && months > 0:
                return `${years} years and ${months} months`;
        }
    }

    public static hasSavingsGoal(props: any): boolean {
        return _.has(props, 'drawerProps.card.attributes.linked_card_id');
    }

    public static getSavingsGoalCards(cards: CardModel[]): CardModel[] {
        return _.filter(cards, (card) => {
            return _.has(card, 'amountSaved') && _.has(card, 'percentSaved');
        });
    }

    public static getSavingsGoal(props: any): CardModel {
        const linkedCardId: number = _.get(props, 'drawerProps.card.attributes.linked_card_id');
        if (!linkedCardId) return null;
        return CardService.getCard(linkedCardId);
    }

    public static getName(card: CardModel): string {
        let name = card.nickname ? card.nickname : card.description;
        name = name.charAt(0).toUpperCase() + name.slice(1);

        return name;
    }

    public static isCurrent(card: CardModel): boolean {
        const now = LocalDate.now();
        return now.isBefore(card.endDate) && now.isAfter(card.startDate.minusDays(1));
    }

    public static isOld(card: CardModel): boolean {
        const now = LocalDate.now();
        return !now.isBefore(card.endDate);
    }

    // Did the user save for this savings goal n months ago where 0 is the current month
    public static didSave(card: CardModel, user: FullUserModel, monthsAgo: number): boolean {
        const firstOfMonth = LocalDate.now().withDayOfMonth(1).minusMonths(monthsAgo);

        if (_.get(card, 'attributes.done' + firstOfMonth.toJSON())) {
            return true;
        }
        const account = _.find(user.accounts, { id: card.accountId });

        if (account) {
            if (firstOfMonth.isBefore(card.startDate)) {
                return true;
            }

            let previousBalance = account.current;
            const balanceHistory: BalanceHistory[] = _.get(account, 'balanceHistory') as any;
            if (balanceHistory) {
                const balance: BalanceHistory = _.find(balanceHistory, { date: firstOfMonth.toJSON() });
                if (balance) previousBalance = balance.balance;
            }

            // how much do you need to save per month for all goals going to this account
            let perMonth = 0;
            const allAccountGoals: CardModel[] = _.filter(user.cards, { accountId: account.id });
            allAccountGoals.forEach((card: CardModel) => {
                perMonth += card.attributes.perMonth as number;
            });

            if (previousBalance + perMonth <= account.current) {
                return true;
            }
        } else {
            return true;
        }

        return false;
    }

    public static async postPhoto(cardId: number, image: Blob): Promise<void> {
        await this.postJpeg(`/photos/card/${cardId}/original`, image);
    }

    public static async getPhoto(cardId: number): Promise<Blob> {
        return await this.get(`/photos/card/${cardId}`);
    }

    public static getPhotoUrl(card: CardModel): string {
        return `${BW_PHANT_URL}/photos/card/${card.id}/${card.hash}/original.jpg`;
    }
}
